import React, { useState, useContext, useEffect, useCallback } from 'react';
import ivclass from '@invideoio/web-shared/Modules/IVClass';
import { useRouter } from 'next/router';
import { disableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';

import { DispatchContext } from '../../store/store';

import { getAssetUrl } from '@utils/common.utils';
import ImageComponent from '@components/common/ImageComponent/ImageComponent';
import { openAuthModal } from 'src/store/actions';
import ExitIntentModal from '@components/exit-intent-modal';
import {
  AmplitudeEvents,
  sendAmplitudeAnalyticEvent,
  sendRudderStackAnalyticEvent,
} from '@utils/analytics';
import { CTASource } from '@components/banner/types';
import { BrowserType } from '@constants/common';
import { PlatformType } from '@invideoio/web-shared/constants/common';
import {
  STICKY_CARD_BG_IMG_PATH,
  STICKY_CARD_BG_IMG_WEBP_PATH,
} from '@constants/assets-path';
import useTranslation from 'next-translate/useTranslation';
import { ClickCtaUxSource, ClickCtaType } from '../../types/analytics';

interface BannerProps {
  hasUserSignedIn: Boolean;
  platform: PlatformType;
  browser: BrowserType;
}

const Banner = ({ hasUserSignedIn, platform, browser }: BannerProps) => {
  const dispatch = useContext(DispatchContext);

  const router = useRouter();

  const { t } = useTranslation('common');

  const [isExitIntentModalOpen, setIsExitIntentModalOpen] =
    useState<Boolean>(false);
  const [isModalShown, setIsModalShown] = useState<Boolean>(false);

  const openExitIntentModal = () => {
    setIsExitIntentModalOpen(x => true);
  };
  const closeExitIntentModal = () => {
    setIsExitIntentModalOpen(false);
    setIsModalShown(true);
  };

  useEffect(() => {
    if (sessionStorage['modalCount'] == null) {
      sessionStorage.setItem('modalCount', '0');
    }

    if (isExitIntentModalOpen && !isModalShown) {
      let count = parseInt(sessionStorage?.getItem('modalCount') || '0');
      sessionStorage.setItem('modalCount', (count += 1).toString());
      disableBodyScroll(document.querySelector('#exitIntentModal')!);
    }
    return () => {
      clearAllBodyScrollLocks();
    };
  }, [isExitIntentModalOpen]);

  useEffect(() => {
    document.body.addEventListener('mouseleave', () => {
      if (parseInt(sessionStorage['modalCount']) < 3) {
        openExitIntentModal();
      }
    });
  }, []);

  const handleClick = (source: CTASource) => {
    if (hasUserSignedIn) {
      const url =
        process.env.NEXT_PUBLIC_STUDIO_HOST + '/workflow/marketing-templates';

      router.push(url);
    } else {
      const sourceToPosition = {
        [CTASource.STICKY_CARD]: 'Blog Sticky Card',
        [CTASource.EXIT_INTENT_MODAL]: 'Blog Exit Intent Modal',
      };
      sendAmplitudeAnalyticEvent(AmplitudeEvents.clickSignUp, {
        btnPosition: sourceToPosition[source],
      });
      const uxSourceToPosition = {
        [CTASource.STICKY_CARD]: ClickCtaUxSource.BlogStickyCard,
        [CTASource.EXIT_INTENT_MODAL]: ClickCtaUxSource.BlogExitIntentModal,
      };
      sendRudderStackAnalyticEvent({
        event_name: 'click_cta',
        event_properties: {
          ux_source: uxSourceToPosition[source],
          type: ClickCtaType.ClickSignup,
          feature_utm: 'na',
        },
      });
      dispatch(openAuthModal({ tab: 'OTP_SIGNUP' }));
    }
  };

  const bgImagePath =
    browser !== BrowserType.Safari
      ? STICKY_CARD_BG_IMG_WEBP_PATH
      : STICKY_CARD_BG_IMG_PATH;

  return (
    <>
      {isExitIntentModalOpen && !isModalShown && (
        <ExitIntentModal
          closeExitIntentModal={closeExitIntentModal}
          buttonAction={handleClick}
          browser={browser}
        />
      )}
      <div
        className={ivclass(
          'iv-rounded-[15px]',
          'iv-sticky-card-banner-bg',
          'iv-h-[320px] lg:iv-h-[290px] md:iv-h-[255px] sm:iv-h-[320px]',
          'lg:iv-max-w-[293px]',
          'iv-w-full ',
          'iv-mb-35 lg:iv-mb-25',
          'iv-relative',
          'iv-overflow-hidden',
        )}
      >
        <ImageComponent
          elementype={'native'}
          loading={platform !== PlatformType.Desktop ? 'lazy' : 'eager'}
          fetchpriority={platform === PlatformType.Desktop ? 'high' : 'auto'}
          src={getAssetUrl('blog', bgImagePath)}
          className={ivclass(
            'iv-absolute',
            'iv-h-[214px] iv-w-[296px]',
            'lg:iv-h-[193px] lg:iv-w-[275px]',
            'md:iv-h-[225px] md:iv-w-[360px]',
            'iv-bottom-0 iv-right-[-45px] lg:iv-bottom-[-20px] xl:iv-bottom-[-20px]',
          )}
        />
        <div
          className={ivclass(
            'iv-text-heading-xs iv-font-extrabold',
            'iv-text-grey-0 ',
            'iv-h-full',
            'iv-flex iv-justify-between iv-flex-col',
            'iv-py-25 iv-px-[28px]',
            'md:iv-pt-30 md:iv-pb-35 md:iv-px-[40px]',
          )}
        >
          <div className={ivclass('iv-max-w-[250px]', 'iv-z-10')}>
            {t('CtxCard.heading')}
          </div>
          <button
            className={ivclass(
              'iv-text-body-2xl iv-font-semibold',
              'iv-bg-grey-0 ',
              'iv-px-[28px] lg:iv-px-[0]',
              'iv-py-[14px] lg:iv-py-[12px] ',
              'iv-text-black-90',
              'iv-rounded-[110px]',
              'iv-max-w-[205px] lg:iv-max-w-[185px] md:iv-max-w-[240px]',
              'iv-z-10',
              'focus:iv-outline-none',
              'iv-whitespace-nowrap',
            )}
            onClick={() => handleClick(CTASource.STICKY_CARD)}
          >
            {hasUserSignedIn ? 'Start with a template' : 'Get started for free'}
          </button>
        </div>
      </div>
    </>
  );
};

export default Banner;
